import { EMPTY, Subject, type Observable } from 'rxjs';

import { exposeToGlobalConsole } from '../react/expose-to-global-console';
import { createEmptyChannelCounts, resolvePopDevToolsChannelForTag } from './channels';
import { getPopDevToolsAvailability, isPopDevToolsEnabled } from './enabled';
import { PopDevToolsMetering } from './metering';
import { PopDevToolsRingBuffer } from './ring-buffer';
import type {
  PopDevToolsApi,
  PopDevToolsChannel,
  PopDevToolsChannelCounts,
  PopDevToolsChannelLogger,
  PopDevToolsEvent,
  PopDevToolsFilter,
  PopDevToolsLevel,
  PopDevToolsLiveState,
  PopDevToolsLiveStateOptions,
  PopDevToolsLiveStateProvider,
  PopDevToolsRecordInput,
  PopDevToolsWindowController,
} from './types';

export interface PopDevToolsServiceOptions {
  capacity?: number;
  now?: () => number;
  autoFlushMetering?: boolean;
}

const DEFAULT_CAPACITY = 50_000;

const createEmptyLiveState = (): PopDevToolsLiveState => ({
  capturedAt: Date.now(),
  connections: [],
  reduxState: {
    expanded: {},
    topLevelKeys: [],
  },
  sessions: [],
});

const noopWindowController: PopDevToolsWindowController = {
  open: () => undefined,
  close: () => undefined,
  toggle: () => undefined,
};

const consoleMethodByLevel: Record<PopDevToolsLevel, 'debug' | 'error' | 'info' | 'warn'> = {
  debug: 'debug',
  error: 'error',
  info: 'info',
  warn: 'warn',
};

// Channel loggers replaced direct console.* diagnostics at their call sites
// (e.g. the [P2P]/[pop-trace] logs in the media connections), so when the tool
// is disabled or compiled out they must still reach the console — otherwise
// shipping builds silently lose those failure-path logs.
const writeToConsoleFallback = (level: PopDevToolsLevel, args: readonly unknown[]) => {
  console[consoleMethodByLevel[level]](...args);
};

export class PopDevToolsService implements PopDevToolsApi {
  private ring: PopDevToolsRingBuffer;
  private readonly metering = new PopDevToolsMetering();
  private readonly subject = new Subject<PopDevToolsEvent>();
  private readonly counts = createEmptyChannelCounts();
  private readonly now: () => number;
  private readonly autoFlushMetering: boolean;
  private windowController?: PopDevToolsWindowController;
  private liveStateProvider?: PopDevToolsLiveStateProvider;
  private flushTimer?: ReturnType<typeof setTimeout>;
  private flushTimerAt?: number;

  constructor(options: PopDevToolsServiceOptions = {}) {
    this.ring = new PopDevToolsRingBuffer(options.capacity ?? DEFAULT_CAPACITY);
    this.now = options.now ?? Date.now;
    this.autoFlushMetering = options.autoFlushMetering ?? true;
  }

  get events$(): Observable<PopDevToolsEvent> {
    return this.subject.asObservable();
  }

  open() {
    this.getWindowController('open').open();
  }

  close() {
    this.getWindowController('close').close();
  }

  toggle() {
    this.getWindowController('toggle').toggle();
  }

  setWindowController(controller: PopDevToolsWindowController) {
    this.windowController = controller;
  }

  hasWindowController() {
    return !!this.windowController;
  }

  setLiveStateProvider(provider: PopDevToolsLiveStateProvider) {
    this.liveStateProvider = provider;
  }

  getLiveState(options?: PopDevToolsLiveStateOptions) {
    return this.liveStateProvider?.(options) ?? createEmptyLiveState();
  }

  record(input: PopDevToolsRecordInput) {
    const normalized = this.normalizeInput(input);
    const result = this.metering.record(normalized, normalized.ts ?? this.now());
    let appended: PopDevToolsEvent | undefined;
    result.summaries.forEach((summary) => {
      appended = this.append(summary);
    });
    if (result.nextFlushAt !== undefined) this.scheduleMeteringFlush(result.nextFlushAt);
    return result.metered ? undefined : appended;
  }

  log(channel: PopDevToolsChannel, level: PopDevToolsLevel, message: string, data?: unknown) {
    this.writeToConsole(level, message, data);
    this.record({ channel, level, message, data });
  }

  channel(name: PopDevToolsChannel): PopDevToolsChannelLogger {
    return {
      debug: (message, ...data) => this.logChannelArgs(name, 'debug', message, data),
      info: (message, ...data) => this.logChannelArgs(name, 'info', message, data),
      warn: (message, ...data) => this.logChannelArgs(name, 'warn', message, data),
      error: (message, ...data) => this.logChannelArgs(name, 'error', message, data),
    };
  }

  mark(label: string) {
    this.record({
      channel: 'console',
      level: 'info',
      message: label,
      data: { label },
      isMarker: true,
    });
  }

  getEvents(filter?: PopDevToolsFilter) {
    return [...this.ring.snapshot(filter)];
  }

  getChannelCounts(): PopDevToolsChannelCounts {
    return { ...this.counts };
  }

  getBufferSize() {
    return this.ring.size;
  }

  getBufferCapacity() {
    return this.ring.capacity;
  }

  setBufferCapacity(capacity: number) {
    if (!Number.isInteger(capacity) || capacity <= 0) return;
    if (capacity === this.ring.capacity) return;

    const retainedEvents = this.ring.snapshot().slice(-capacity);
    this.ring = new PopDevToolsRingBuffer(capacity);
    Object.assign(this.counts, createEmptyChannelCounts());
    retainedEvents.forEach((event) => {
      const { event: appended } = this.ring.append(event);
      this.counts[appended.channel] += 1;
    });
  }

  getLowercaseMessage(event: PopDevToolsEvent) {
    return this.ring.getLowercaseMessage(event);
  }

  exportNdjson(filter?: PopDevToolsFilter) {
    return this.getEvents(filter)
      .map((event) => JSON.stringify(event))
      .join('\n');
  }

  setMetering(stream: string, enabled: boolean) {
    if (!enabled) this.flushMetering();
    this.metering.setMetering(stream, enabled);
  }

  getMeteredStreams() {
    return this.metering.getMeteredStreams();
  }

  flushMetering() {
    this.clearMeteringFlushTimer();
    this.metering.flushAll(this.now()).forEach((summary) => this.append(summary));
  }

  clear() {
    this.clearMeteringFlushTimer();
    this.metering.clear();
    this.ring.clear();
    const freshCounts = createEmptyChannelCounts();
    Object.assign(this.counts, freshCounts);
  }

  destroy() {
    this.clearMeteringFlushTimer();
    this.subject.complete();
  }

  private append(input: PopDevToolsRecordInput) {
    const { streamKey: _streamKey, ...draft } = this.normalizeInput(input);
    const { event, dropped } = this.ring.append(draft);
    if (dropped) this.counts[dropped.channel] -= 1;
    this.counts[event.channel] += 1;
    this.subject.next(event);
    return event;
  }

  private normalizeInput(input: PopDevToolsRecordInput) {
    return {
      ts: input.ts ?? this.now(),
      source: input.source ?? 'renderer',
      windowType: input.windowType,
      channel: input.channel ?? (input.tag ? resolvePopDevToolsChannelForTag(input.tag) : 'console'),
      level: input.level ?? 'info',
      message: input.message,
      via: input.via,
      data: input.data,
      sessionId: input.sessionId,
      peerId: input.peerId,
      trackId: input.trackId,
      count: input.count,
      isMarker: input.isMarker,
      streamKey: input.streamKey,
    } satisfies PopDevToolsRecordInput;
  }

  private writeToConsole(level: PopDevToolsLevel, message: string, data?: unknown) {
    const method = consoleMethodByLevel[level];
    const originalConsole = (window as any).__popDevToolsOriginalConsole as
      | Partial<Record<typeof method, (...args: unknown[]) => void>>
      | undefined;
    const writer = originalConsole?.[method] ?? console[method].bind(console);
    if (data === undefined) writer(message);
    else writer(message, data);
  }

  private writeArgsToConsole(level: PopDevToolsLevel, args: readonly unknown[]) {
    const method = consoleMethodByLevel[level];
    const originalConsole = (window as any).__popDevToolsOriginalConsole as
      | Partial<Record<typeof method, (...args: unknown[]) => void>>
      | undefined;
    const writer = originalConsole?.[method] ?? console[method].bind(console);
    writer(...args);
  }

  private logChannelArgs(
    channel: PopDevToolsChannel,
    level: PopDevToolsLevel,
    message: string,
    data: readonly unknown[],
  ) {
    this.writeArgsToConsole(level, [message, ...data]);
    this.record({
      channel,
      level,
      message,
      data: data.length === 0 ? undefined : data.length === 1 ? data[0] : data,
    });
  }

  private getWindowController(action: keyof PopDevToolsWindowController) {
    if (this.windowController) return this.windowController;

    return {
      ...noopWindowController,
      [action]: () => {
        this.record({
          channel: 'console',
          level: 'warn',
          message: 'devtools window not built yet',
          data: { action },
        });
      },
    };
  }

  private scheduleMeteringFlush(flushAt: number) {
    if (!this.autoFlushMetering) return;
    if (this.flushTimer && this.flushTimerAt !== undefined && this.flushTimerAt <= flushAt) return;

    this.clearMeteringFlushTimer();
    this.flushTimerAt = flushAt;
    this.flushTimer = setTimeout(
      () => {
        this.flushTimer = undefined;
        this.flushTimerAt = undefined;
        this.metering.flushDue(this.now()).forEach((summary) => this.append(summary));
        const nextFlushAt = this.metering.getNextFlushAt();
        if (nextFlushAt !== undefined) this.scheduleMeteringFlush(nextFlushAt);
      },
      Math.max(0, flushAt - this.now()),
    );
  }

  private clearMeteringFlushTimer() {
    if (!this.flushTimer) return;
    clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
    this.flushTimerAt = undefined;
  }
}

let service: PopDevToolsService | undefined;
// Providers can register while the tool is disabled (app-store sets the live
// state provider at store creation); stash them so a later runtime opt-in
// (see runtime-opt-in.ts) constructs a fully wired service.
let pendingLiveStateProvider: PopDevToolsLiveStateProvider | undefined;

const getServiceIfEnabled = () => {
  if (!isPopDevToolsEnabled()) return undefined;
  if (!service) {
    service = new PopDevToolsService();
    if (pendingLiveStateProvider) service.setLiveStateProvider(pendingLiveStateProvider);
  }
  return service;
};

// Runtime opt-out (beta/prod debug page): drop the buffered events and the
// service so a disabled app run holds no captured data.
export const teardownPopDevToolsService = () => {
  service?.clear();
  service = undefined;
};

export const getPopDevToolsService = () => service;

const getPopDevToolsDebugState = () => ({
  ...getPopDevToolsAvailability(),
  serviceCreated: !!service,
  windowControllerRegistered: !!service?.hasWindowController(),
  bufferSize: service?.getBufferSize() ?? 0,
  eventCount: service?.getEvents().length ?? 0,
});

export const initializePopDevTools = () => {
  // The debug getter stays available whenever DevTools code is compiled in, so
  // "why is DevTools disabled here?" can be answered from the console.
  Object.defineProperty(window, '__popDevToolsDebug', {
    configurable: true,
    get: getPopDevToolsDebugState,
  });

  const activeService = getServiceIfEnabled();
  if (!activeService) return undefined;

  window.popDevTools = popDevTools;
  exposeToGlobalConsole({ popDevTools: popDevTools });
  return activeService;
};

export const popDevTools: PopDevToolsApi & {
  record(input: PopDevToolsRecordInput): PopDevToolsEvent | undefined;
  setWindowController(controller: PopDevToolsWindowController): void;
  getChannelCounts(): PopDevToolsChannelCounts;
  getBufferSize(): number;
  getBufferCapacity(): number;
  setBufferCapacity(capacity: number): void;
  setLiveStateProvider(provider: PopDevToolsLiveStateProvider): void;
  getLiveState(options?: PopDevToolsLiveStateOptions): PopDevToolsLiveState;
  getMeteredStreams(): Map<string, boolean>;
  flushMetering(): void;
  clear(): void;
} = {
  open: () => getServiceIfEnabled()?.open(),
  close: () => service?.close(),
  toggle: () => getServiceIfEnabled()?.toggle(),
  log: (channel, level, message, data) => {
    getServiceIfEnabled()?.log(channel, level, message, data);
  },
  channel: (name) => {
    const logThrough =
      (level: PopDevToolsLevel) =>
      (message: string, ...data: unknown[]) => {
        const service = getServiceIfEnabled();
        if (service) service.channel(name)[level](message, ...data);
        else writeToConsoleFallback(level, [message, ...data]);
      };
    return {
      debug: logThrough('debug'),
      info: logThrough('info'),
      warn: logThrough('warn'),
      error: logThrough('error'),
    };
  },
  mark: (label) => {
    getServiceIfEnabled()?.mark(label);
  },
  getEvents: (filter) => getServiceIfEnabled()?.getEvents(filter) ?? [],
  get events$() {
    return service?.events$ ?? EMPTY;
  },
  exportNdjson: (filter) => getServiceIfEnabled()?.exportNdjson(filter) ?? '',
  setMetering: (stream, enabled) => {
    getServiceIfEnabled()?.setMetering(stream, enabled);
  },
  record: (input) => getServiceIfEnabled()?.record(input),
  setWindowController: (controller) => {
    getServiceIfEnabled()?.setWindowController(controller);
  },
  setLiveStateProvider: (provider) => {
    pendingLiveStateProvider = provider;
    getServiceIfEnabled()?.setLiveStateProvider(provider);
  },
  getLiveState: (options) => getServiceIfEnabled()?.getLiveState(options) ?? createEmptyLiveState(),
  getChannelCounts: () => getServiceIfEnabled()?.getChannelCounts() ?? createEmptyChannelCounts(),
  getBufferSize: () => getServiceIfEnabled()?.getBufferSize() ?? 0,
  getBufferCapacity: () => getServiceIfEnabled()?.getBufferCapacity() ?? DEFAULT_CAPACITY,
  setBufferCapacity: (capacity) => {
    getServiceIfEnabled()?.setBufferCapacity(capacity);
  },
  getMeteredStreams: () => getServiceIfEnabled()?.getMeteredStreams() ?? new Map(),
  flushMetering: () => {
    service?.flushMetering();
  },
  clear: () => {
    service?.clear();
  },
};
